/** Week/month aggregates exactly as plan D6. Estimated revenue is by price list (payments are mocked). */
import { addDaysToKey, nextMonthKey, startOfDayMs, startOfMonthKey, startOfWeekKey, toMs, zonedParts } from './time';
import type { Agorot, Appointment, DayKey, Id, PaymentMethod, Service } from './types';

export type ReportPeriodKind = 'week' | 'month';
export interface ReportPeriod { kind: ReportPeriodKind; from: DayKey; to: DayKey; startMs: number; endMs: number }
export interface TopService { serviceId: Id; name: string; count: number; revenueAgorot: Agorot }
export interface ReportData {
  period: ReportPeriod;
  /** Booked appointments in the period (excludes cancelled and unpaid holds). */
  appointments: number;
  cancelled: number;
  noShows: number;
  /** Pending + confirmed + completed in the period at list price (same rule as Today's expected income). */
  revenueAgorot: Agorot;
  /** no-shows / (completed + no-shows + confirmed-and-past); null when nothing has happened yet. */
  noShowRate: number | null;
  clients: { new: number; returning: number };
  topServices: TopService[];
  /** 24 entries, local hour 0..23. */
  busiestHours: Array<{ hour: number; count: number }>;
  peakHour: number | null;
  refilledViaWaitlist: number;
  /** Same as `revenueAgorot`: what the period is worth at price list. */
  expectedAgorot: Agorot;
  /** Money actually recorded: paid deposits plus what markPaid captured, by method. */
  collectedAgorot: Agorot;
  byMethod: Record<PaymentMethod, Agorot>;
}

/** Collected money of one appointment by method: a paid deposit (by depositMethod, default card) plus the recorded payment. */
export function collectedByMethod(a: Appointment): Array<[PaymentMethod, Agorot]> {
  const out: Array<[PaymentMethod, Agorot]> = [];
  if (a.depositStatus === 'paid' && a.depositAgorot > 0) out.push([a.depositMethod ?? 'card', a.depositAgorot]);
  const paid = a.payment?.paidAgorot ?? a.paidAgorot;
  if (paid !== undefined && paid > 0) out.push([a.payment?.method ?? 'other', paid]);
  return out;
}

export function reportPeriod(kind: ReportPeriodKind, anchor: DayKey, tz: string): ReportPeriod {
  const from = kind === 'week' ? startOfWeekKey(anchor) : startOfMonthKey(anchor);
  const next = kind === 'week' ? addDaysToKey(from, 7) : nextMonthKey(from);
  return { kind, from, to: addDaysToKey(next, -1), startMs: startOfDayMs(from, tz), endMs: startOfDayMs(next, tz) };
}

/** Statuses that count as income at price list. The single definition used by Today's expected income and by the reports. */
export const INCOME_STATUSES: ReadonlySet<string> = new Set(['pending', 'confirmed', 'completed']);
export const incomeAgorot = (appointments: Appointment[]): Agorot =>
  appointments.filter((a) => INCOME_STATUSES.has(a.status)).reduce((sum, a) => sum + a.priceAgorot, 0);

const BOOKED = new Set(['pending', 'confirmed', 'completed', 'no_show']);
const isCancelled = (a: Appointment): boolean => a.status === 'cancelled_client' || a.status === 'cancelled_owner';

export function buildReport(input: {
  appointments: Appointment[]; services: Service[]; kind: ReportPeriodKind; anchor: DayKey; nowMs: number; tz: string;
}): ReportData {
  const period = reportPeriod(input.kind, input.anchor, input.tz);
  const inPeriod = (a: Appointment): boolean => toMs(a.start) >= period.startMs && toMs(a.start) < period.endMs;
  const mine = input.appointments.filter(inPeriod);
  const booked = mine.filter((a) => BOOKED.has(a.status));
  const earned = booked.filter((a) => a.status === 'completed' || (a.status === 'confirmed' && toMs(a.end) <= input.nowMs));
  const noShows = booked.filter((a) => a.status === 'no_show');
  const outcomes = earned.length + noShows.length;

  const firstBefore = new Set(
    input.appointments.filter((a) => BOOKED.has(a.status) && toMs(a.start) < period.startMs).map((a) => a.clientId),
  );
  const clientIds = new Set(booked.map((a) => a.clientId));
  const newClients = [...clientIds].filter((id) => !firstBefore.has(id)).length;

  const hours = Array.from({ length: 24 }, (_, hour) => ({ hour, count: 0 }));
  for (const a of booked) {
    const slot = hours[zonedParts(toMs(a.start), input.tz).hour];
    if (slot) slot.count++;
  }
  const peak = hours.reduce((best, h) => (h.count > best.count ? h : best), { hour: -1, count: 0 });

  const byService = new Map<Id, TopService>();
  for (const a of booked) {
    const cur = byService.get(a.serviceId) ?? { serviceId: a.serviceId, name: input.services.find((s) => s.id === a.serviceId)?.name ?? '', count: 0, revenueAgorot: 0 };
    cur.count++;
    if (INCOME_STATUSES.has(a.status)) cur.revenueAgorot += a.priceAgorot;
    byService.set(a.serviceId, cur);
  }
  const topServices = [...byService.values()]
    .sort((a, b) => b.count - a.count || b.revenueAgorot - a.revenueAgorot || a.name.localeCompare(b.name, 'he'))
    .slice(0, 3);

  const byMethod: Record<PaymentMethod, Agorot> = { cash: 0, card: 0, bit: 0, paybox: 0, other: 0 };
  for (const a of booked) for (const [m, v] of collectedByMethod(a)) byMethod[m] += v;

  return {
    period,
    appointments: booked.length,
    cancelled: mine.filter(isCancelled).length,
    noShows: noShows.length,
    revenueAgorot: incomeAgorot(booked),
    noShowRate: outcomes === 0 ? null : noShows.length / outcomes,
    clients: { new: newClients, returning: clientIds.size - newClients },
    topServices,
    busiestHours: hours,
    peakHour: peak.hour < 0 ? null : peak.hour,
    refilledViaWaitlist: booked.filter((a) => a.source === 'waitlist').length,
    expectedAgorot: incomeAgorot(booked),
    collectedAgorot: Object.values(byMethod).reduce((x, y) => x + y, 0),
    byMethod,
  };
}

